import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import api from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import {
  BuildingOfficeIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BanknotesIcon,
  CalendarIcon,
  UserGroupIcon,
  BriefcaseIcon,
  IdentificationIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const DEFAULT_FORM = {
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  taxId: '',
  passportId: '',
  typePaiement: 'mensuel',

  forfait: '',
  soldeInitial: '',

  dateAnniversaire: '',

  facturationAuto: false,

  logoUrl: '',
  formeJuridique: '',
  natureActivite: 'commercial',
  status: 'actif',

  clientCollaborators: {
    executor: '',
    teamLead: '',
  },
};

const normalizeDateToInput = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

// ✅ Fonction pour formater une date ISO en dd/mm/yyyy
const formatDateDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
};

const extractUserId = (val) => {
  if (!val) return '';
  if (typeof val === 'object') return val._id || val.id || '';
  return String(val);
};

// ✅ Autorise uniquement chiffres + , + . + -
const sanitizeDecimalTyping = (val) => {
  if (val === null || val === undefined) return '';
  return String(val).replace(/[^0-9,.\-]/g, '');
};

// ✅ Convertit "201,254" ou "201.254" en Number
const parseDecimalInput = (val) => {
  if (val === null || val === undefined) return 0;

  let s = String(val).trim();
  if (s === '') return 0;

  // enlever espaces
  s = s.replace(/\s/g, '');

  // virgule -> point
  s = s.replace(/,/g, '.');

  // plusieurs points => garder le 1er comme séparateur décimal
  const parts = s.split('.');
  if (parts.length > 2) {
    s = parts[0] + '.' + parts.slice(1).join('');
  }

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// ✅ parse optionnel: renvoie undefined si vide (pour ne pas envoyer au backend)
const parseOptionalDecimal = (val) => {
  const s = val === null || val === undefined ? '' : String(val).trim();
  if (s === '') return undefined;
  return parseDecimalInput(s);
};

// ✅ Composants UI déplacés EN DEHORS pour éviter les re-renders
const SectionTitle = ({ title, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-100 mt-3 first:mt-0">
    <Icon className="w-4 h-4 text-indigo-500" />
    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
  </div>
);

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
};

const ClientForm = ({
  onSuccess = () => {},
  onCancel = () => {},
  initialData = null
}) => {
  const { tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  const isEditMode = initialData && initialData._id;

  // Hydratation du formulaire
  useEffect(() => {
    if (!initialData || !initialData._id) {
      setFormData(DEFAULT_FORM);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...DEFAULT_FORM,
      ...initialData,

      // ✅ hydrate en STRING
      forfait:
        initialData.forfait !== undefined && initialData.forfait !== null
          ? String(initialData.forfait)
          : '',
      soldeInitial:
        initialData.soldeInitial !== undefined && initialData.soldeInitial !== null
          ? String(initialData.soldeInitial)
          : '',

      dateAnniversaire: normalizeDateToInput(initialData.dateAnniversaire),

      // ✅ si existant on garde, sinon false par défaut
      facturationAuto:
        typeof initialData.facturationAuto === 'boolean'
          ? initialData.facturationAuto
          : false,
       // ✅ AJOUTS ICI
      logoUrl: initialData.logoUrl || '',
      formeJuridique: initialData.formeJuridique || '',
      natureActivite: initialData.natureActivite || 'commercial',
      status: initialData.status || 'actif',
      clientCollaborators: {
        executor: extractUserId(initialData.clientCollaborators?.executor),
        teamLead: extractUserId(initialData.clientCollaborators?.teamLead),
      },

      prochaineFacturation: initialData.prochaineFacturation
        ? normalizeDateToInput(initialData.prochaineFacturation)
        : prev.prochaineFacturation,
    }));
  }, [initialData]);

   

      
  // Chargement des collaborateurs + admins (exclure clients)
  useEffect(() => {
    const loadTeamMembers = async () => {
      setLoadingTeamMembers(true);
      try {
        const [collaboratorsRes, adminsRes] = await Promise.all([
          api.get('/users?role=collaborator'),
          api.get('/users?role=admin')
        ]);

        const collaborators = Array.isArray(collaboratorsRes.data) ? collaboratorsRes.data : [];
        const admins = Array.isArray(adminsRes.data) ? adminsRes.data : [];

        const allMembers = [...collaborators, ...admins];

        const uniqueMembers = allMembers.reduce((acc, user) => {
          if (user.role === 'client') return acc;
          if (!acc.find(u => u._id === user._id)) acc.push(user);
          return acc;
        }, []);

        uniqueMembers.sort((a, b) => {
          if (a.role === 'admin' && b.role !== 'admin') return -1;
          if (a.role !== 'admin' && b.role === 'admin') return 1;
          const nameA = (a.name || a.email || '').toLowerCase();
          const nameB = (b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setTeamMembers(uniqueMembers);
      } catch (err) {
        console.error('Erreur chargement membres équipe:', err);
        setTeamMembers([]);
      } finally {
        setLoadingTeamMembers(false);
      }
    };

    loadTeamMembers();
  }, []);

  // Calcul prochaine facturation
  const calculerProchaineFacturation = useCallback((dateAnniversaire, typePaiement) => {
    if (!dateAnniversaire) return '';
    try {
      let date = new Date(dateAnniversaire);
      const aujourdhui = new Date();
      aujourdhui.setHours(0, 0, 0, 0);

      if (isNaN(date.getTime())) return '';
      if (date > aujourdhui) return date.toISOString().split('T')[0];

      while (date <= aujourdhui) {
        switch (typePaiement) {
          case 'mensuel': date.setMonth(date.getMonth() + 1); break;
          case 'trimestriel': date.setMonth(date.getMonth() + 3); break;
          case 'semestriel': date.setMonth(date.getMonth() + 6); break;
          case 'annuel': date.setFullYear(date.getFullYear() + 1); break;
          default: date.setMonth(date.getMonth() + 1);
        }
      }
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('clientCollaborators.')) {
      const key = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        clientCollaborators: { ...prev.clientCollaborators, [key]: value },
      }));
      return;
    }

    setFormData((prev) => {
      let newValue;

      if (type === 'checkbox') {
        newValue = checked;
      } else if (name === 'forfait' || name === 'soldeInitial') {
        // ✅ garde la saisie libre (virgule autorisée)
        newValue = sanitizeDecimalTyping(value);
      } else {
        newValue = value;
      }

      const newFormData = { ...prev, [name]: newValue };

      if (name === 'dateAnniversaire' || name === 'typePaiement') {
        newFormData.prochaineFacturation = calculerProchaineFacturation(
          name === 'dateAnniversaire' ? value : prev.dateAnniversaire,
          name === 'typePaiement' ? value : prev.typePaiement
        );
      }

      return newFormData;
    });
  }, [calculerProchaineFacturation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const cleanCollaborators = {};
      if (formData.clientCollaborators?.executor?.trim()) {
        cleanCollaborators.executor = formData.clientCollaborators.executor;
      }
      if (formData.clientCollaborators?.teamLead?.trim()) {
        cleanCollaborators.teamLead = formData.clientCollaborators.teamLead;
      }

      const forfaitNum = parseOptionalDecimal(formData.forfait);       // undefined si vide
      const soldeInitialNum = parseDecimalInput(formData.soldeInitial); // 0 si vide

      const clientData = {
        ...formData,
        companyName: (formData.companyName || '').trim(),
        contactName: (formData.contactName || '').trim(),
        email: (formData.email || '').trim(),
        phone: (formData.phone || '').trim(),
        address: (formData.address || '').trim(),
        taxId: (formData.taxId || '').trim(),
        passportId: (formData.passportId || '').trim(),

        // ✅ on envoie seulement si rempli
        ...(forfaitNum !== undefined ? { forfait: forfaitNum } : {}),

        soldeInitial: soldeInitialNum,

        soldeActuel: isEditMode
          ? (parseDecimalInput(initialData?.soldeActuel ?? soldeInitialNum))
          : soldeInitialNum,

        // ✅ Date anniversaire non obligatoire => on n'envoie pas si vide
        ...(formData.dateAnniversaire ? { dateAnniversaire: formData.dateAnniversaire } : {}),

        clientCollaborators: Object.keys(cleanCollaborators).length > 0 ? cleanCollaborators : undefined,
      };

      // Validations côté client
      if (!clientData.companyName) throw new Error("Le nom est obligatoire");
      if (!isMedical && !clientData.taxId) throw new Error("L'identifiant fiscal est obligatoire");
      if (formData.facturationAuto && !formData.dateAnniversaire) {
        throw new Error("La date de début est obligatoire quand la facturation automatique est activée");
      }
      // ✅ plus de validation sur dateAnniversaire
      // ✅ plus de validation sur forfait

      if (isEditMode) {
        await api.put(`/clients/${initialData._id}`, clientData);
      } else {
        await api.post('/clients', clientData);
      }

      onSuccess();
    } catch (error) {
      let errMsg = 'Erreur lors de la sauvegarde';

      if (error.response) {
        const { data } = error.response;
        if (data) {
          if (data.details && Array.isArray(data.details) && data.details.length > 0) {
            errMsg = data.details.join('\n');
          } else if (data.error) {
            errMsg = data.error;
          } else if (data.message) {
            errMsg = data.message;
          } else if (typeof data === 'string') {
            errMsg = data;
          }
        }
      } else if (error.message) {
        errMsg = error.message;
      }

      setErrorMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const prochaineFacturationApercu =
    formData.prochaineFacturation ||
    calculerProchaineFacturation(formData.dateAnniversaire, formData.typePaiement);

  const inputClass =
    "w-full px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder-gray-400 text-sm";
  const labelClass = "block text-xs font-medium text-gray-500 mb-0.5";

  const getUserDisplayName = (user) => {
    const name = user.name || user.email || 'Sans nom';
    const roleLabel = user.role === 'admin' ? ' (Admin)' : '';
    return `${name}${roleLabel}`;
  };

  return (
    <>
      {errorMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-red-50 px-5 py-4 border-b border-red-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-red-800">Erreur de validation</h3>
              </div>
            </div>

            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 mb-3">Veuillez corriger les erreurs suivantes :</p>
              <ul className="space-y-2">
                {errorMessage.split('\n').map((err, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{err}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setErrorMessage('')}
                className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
              >
                OK, j'ai compris
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">

        {/* 1. INFOS CLIENT */}
        <SectionTitle title={isMedical ? 'Patient' : 'Entreprise'} icon={BuildingOfficeIcon} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className={labelClass}>
              {isMedical ? 'Nom du patient' : 'Raison Sociale'} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className={inputClass}
              placeholder={isMedical ? 'Ex: Jean Dupont' : "Ex: Société Tunisienne de..."}
              required
            />
          </div>

          <div>
            <label className={labelClass}>
              Matricule Fiscal {!isMedical && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              name="taxId"
              value={formData.taxId}
              onChange={handleInputChange}
              className={inputClass}
              placeholder={isMedical ? 'Optionnel' : '1234567ABC000'}
              title={isMedical ? '' : "Format: 7 chiffres + 3 lettres + 3 chiffres"}
              required={!isMedical}
            />
          </div>

          {isMedical && (
            <div>
              <label className={labelClass}>
                CIN OU PASSEPORT
              </label>
              <input
                type="text"
                name="passportId"
                value={formData.passportId}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="Ex: 12345678 / AB123456"
              />
            </div>
          )}

          <div className="md:col-span-2">
            <label className={labelClass}>Adresse Complète</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className={inputClass}
              placeholder="Rue, Ville, Code Postal"
            />
          </div>

          {isMedical && (
            <>
              <div className="relative">
                <label className={labelClass}>Email</label>
                <div className="relative">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`${inputClass} pl-9`}
                    placeholder="patient@exemple.tn"
                  />
                </div>
              </div>

              <div className="relative">
                <label className={labelClass}>Téléphone</label>
                <div className="relative">
                  <PhoneIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2" />
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`${inputClass} pl-9`}
                    placeholder="+216 ..."
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {!isMedical && (
          <>
            <SectionTitle title="Informations Juridiques & Statut" icon={BriefcaseIcon} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              
              <div className="md:col-span-1">
                <label className={labelClass}>URL du Logo</label>
                <div className="relative">
                  <PhotoIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2" />
                  <input
                    type="text"
                    name="logoUrl"
                    value={formData.logoUrl}
                    onChange={handleInputChange}
                    className={`${inputClass} pl-9`}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Forme Juridique</label>
                <input
                  type="text"
                  name="formeJuridique"
                  value={formData.formeJuridique}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Ex: SARL, SUARL, SA..."
                />
              </div>

              <div>
                <label className={labelClass}>Nature de l'activité</label>
                <select
                  name="natureActivite"
                  value={formData.natureActivite}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="commercial">Commercial</option>
                  <option value="profession_liberale">Profession Libérale</option>
                  <option value="service">Service / Autre</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Statut Client</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className={`${inputClass} font-bold ${
                    formData.status === 'actif' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  <option value="actif">🟢 Actif</option>
                  <option value="en_sommeil">🟡 En sommeil</option>
                  <option value="ferme">🔴 Fermé (Clôturé)</option>
                  <option value="quitte_cabinet">🚫 Quitté le cabinet</option>
                </select>
              </div>
            </div>
          </>
        )}

        {!isMedical && (
          <>
            <SectionTitle title="Contact Principal" icon={UserIcon} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className={labelClass}>Nom du Contact</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Ex: M. Ben Ali"
                />
              </div>

              <div className="relative">
                <label className={labelClass}>Email</label>
                <div className="relative">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`${inputClass} pl-9`}
                    placeholder="contact@exemple.tn"
                  />
                </div>
              </div>

              <div className="relative">
                <label className={labelClass}>Téléphone</label>
                <div className="relative">
                  <PhoneIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2" />
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`${inputClass} pl-9`}
                    placeholder="+216 ..."
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {!isMedical && (
          <>
            <SectionTitle title="Contrat & Facturation" icon={BanknotesIcon} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">

              <div>
                <label className={labelClass}>
                  Date de Début
                  {formData.facturationAuto && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="date"
                  name="dateAnniversaire"
                  value={formData.dateAnniversaire}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Fréquence Paiement</label>
                <select
                  name="typePaiement"
                  value={formData.typePaiement}
                  onChange={handleInputChange}
                  className={inputClass}
                >
                  <option value="mensuel">Mensuel</option>
                  <option value="trimestriel">Trimestriel</option>
                  <option value="semestriel">Semestriel</option>
                  <option value="annuel">Annuel</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Forfait HT (DT)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="forfait"
                  value={formData.forfait}
                  onChange={handleInputChange}
                  className={`${inputClass} font-mono font-medium text-indigo-600`}
                  placeholder="Ex: 201,254"
                />
              </div>

              <div>
                <label className={labelClass}>Solde Initial (DT)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="soldeInitial"
                  value={formData.soldeInitial}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="Ex: 0,000"
                />
              </div>

              <div className="opacity-70">
                <label className={`${labelClass} flex items-center gap-1`}>
                  <CalendarIcon className="w-3 h-3" /> Prochaine Fact.
                </label>
                <input
                  value={formatDateDisplay(prochaineFacturationApercu)}
                  readOnly
                  className="w-full px-3 py-1 bg-gray-100 border border-gray-200 rounded-lg text-gray-500 text-sm cursor-not-allowed"
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center cursor-pointer select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      name="facturationAuto"
                      className="sr-only"
                      checked={!!formData.facturationAuto}
                      onChange={handleInputChange}
                    />
                    <div className={`block w-8 h-4 rounded-full transition-colors ${formData.facturationAuto ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                    <div className={`dot absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition-transform ${formData.facturationAuto ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                  <span className="ml-2 text-xs font-medium text-gray-700">Fact. Auto</span>
                </label>
              </div>
            </div>
          </>
        )}

        {!isMedical && (
          <>
            <SectionTitle title="Équipe Affectée" icon={UserGroupIcon} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Dossier géré par (Exécuteur)</label>
                <select
                  name="clientCollaborators.executor"
                  value={formData.clientCollaborators?.executor || ''}
                  onChange={handleInputChange}
                  className={inputClass}
                  disabled={loadingTeamMembers}
                >
                  <option value="">-- Non assigné --</option>
                  {teamMembers.map((u) => (
                    <option key={u._id} value={u._id}>{getUserDisplayName(u)}</option>
                  ))}
                </select>
                {loadingTeamMembers && (
                  <p className="text-xs text-gray-400 mt-1">Chargement...</p>
                )}
              </div>

              <div>
                <label className={labelClass}>Supervisé par (Chef d'équipe)</label>
                <select
                  name="clientCollaborators.teamLead"
                  value={formData.clientCollaborators?.teamLead || ''}
                  onChange={handleInputChange}
                  className={inputClass}
                  disabled={loadingTeamMembers}
                >
                  <option value="">-- Non assigné --</option>
                  {teamMembers.map((u) => (
                    <option key={u._id} value={u._id}>{getUserDisplayName(u)}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* ACTIONS */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 mt-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-gray-600 font-medium hover:bg-gray-100 transition-colors text-sm border border-gray-200"
            disabled={loading}
          >
            Annuler
          </button>

          <button
            type="submit"
            className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-lg shadow-indigo-500/30 transition-all text-sm flex items-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sauvegarde...
              </>
            ) : (
              isEditMode ? 'Mettre à jour' : 'Créer le client'
            )}
          </button>
        </div>
      </form>
    </>
  );
};

ClientForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  initialData: PropTypes.shape({
    _id: PropTypes.string,
    companyName: PropTypes.string,
    contactName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    taxId: PropTypes.string,
    typePaiement: PropTypes.string,
    forfait: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    soldeInitial: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    soldeActuel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dateAnniversaire: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    facturationAuto: PropTypes.bool,
    prochaineFacturation: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    logoUrl: PropTypes.string,
    formeJuridique: PropTypes.string,
    natureActivite: PropTypes.string,
    status: PropTypes.string,
    clientCollaborators: PropTypes.shape({
      executor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      teamLead: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  }),
};

export default ClientForm;
